// Hrana over HTTP (SYNC.md): one statement per pipeline request, values encoded as libSQL does (integers as strings).

export type Value = null | { int: number } | number | string;
/** A value as the app uses it: numbers that must travel as integers are wrapped with `I(n)`. */
export const I = (n: number): Value => ({ int: Math.trunc(n) });

export type Cell = { type: "null" } | { type: "integer"; value: string } | { type: "float"; value: number } | { type: "text"; value: string } | { type: "blob"; base64: string };

export function encode(v: Value | boolean | undefined): Cell {
  if (v === null || v === undefined) return { type: "null" };
  if (typeof v === "boolean") return { type: "integer", value: v ? "1" : "0" };
  if (typeof v === "object") return { type: "integer", value: String(v.int) };
  if (typeof v === "number") return { type: "float", value: v };
  return { type: "text", value: v };
}

export type Row = Record<string, number | string | null>;

export function decode(c: any): number | string | null {
  switch (c?.type) {
    case "integer": return typeof c.value === "number" ? c.value : parseInt(String(c.value), 10);
    case "float": return typeof c.value === "number" ? c.value : parseFloat(String(c.value));
    case "text": return String(c.value ?? "");
    default: return null;
  }
}

export function body(sql: string, args: (Value | boolean)[]): object {
  return { requests: [{ type: "execute", stmt: { sql, args: args.map(encode) } }, { type: "close" }] };
}

export class SyncError extends Error {
  constructor(message: string, readonly kind: "unauthorized" | "http" | "database" | "unavailable" = "database") { super(message); }
}

/** The first result of a pipeline reply → rows keyed by column name. */
export function parseResult(reply: any): Row[] {
  const first = reply?.results?.[0];
  if (!first) throw new SyncError("Sync database error: empty reply", "http");
  if (first.type !== "ok") throw new SyncError("Sync database error: " + (first.error?.message ?? "unknown error"));
  const result = first.response?.result;
  if (!result) throw new SyncError("Sync database error: no result");
  const names: string[] = (result.cols ?? []).map((c: any) => c?.name ?? "");
  return (result.rows ?? []).map((cells: any[]) => {
    const row: Row = {};
    cells.forEach((cell, i) => { if (!(names[i] in row)) row[names[i]] = decode(cell); });
    return row;
  });
}

export const int = (v: unknown): number => (typeof v === "number" ? Math.trunc(v) : typeof v === "string" ? parseInt(v, 10) || 0 : 0);
export const num = (v: unknown): number => (typeof v === "number" ? v : typeof v === "string" ? parseFloat(v) || 0 : 0);
export const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
